<!-- =========================================================================================
  File Name: UserView.vue
  Description: User View page

========================================================================================== -->

<template>
  <div id="page-user-view">

    <vs-alert v-if="errorOccurred" color="danger" title="Course Not Found">
      <span>Course record with id:  not found. </span>
      <span>
        <span>Check </span><router-link :to="{name:'management-list-course'}"
                                        class="text-inherit underline">All Courses</router-link>
      </span>
    </vs-alert>

    <div v-else id="course-data">

      <!--- TOP COURSE BANNER START --->
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <div class="knowledge-base-jumbotron bg-cover rounded-lg vx-card"
               :style="`background-image: url(${courseData.banner})`">
            <div class="knowledge-base-jumbotron-content rounded-lg p-8 lg:p-12" style="backdrop-filter: blur(5px)">
              <div class="flex justify-between items-center ">
                <div>
                  <h1 class="text-white w-full" style="font-size: 4rem">{{ courseData.name }}</h1>
                </div>
                <img class="rounded" :src="courseData.thumbnail" v-if="courseData.thumbnail" width="150px">
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--- TOP COURSE BANNER END --->

      <!--- COURSE ACTION BAR START --->
      <div class="vx-row mb-6 match-height">
        <div class="vx-col grid grid-cols-4 gap-8 w-full">
          <vx-card class="bg-success-gradient p-6 cursor-pointer" @click="addStudentSearchActive = true">
            <div class="flex items-center justify-center">
              <feather-icon icon="UserPlusIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4">Assign<br>Student</h2>
            </div>
          </vx-card>
          <vx-card class="bg-warning-gradient p-6 cursor-pointer" @click="$router.push(`/a/courses/${courseData.id}/subscriptions`)">
            <div class="flex items-center justify-center">
              <feather-icon icon="CreditCardIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4">Subscriptions<br>Manager</h2>
            </div>
          </vx-card>
          <vx-card class="bg-primary-gradient p-6 cursor-pointer" @click="$router.push(`/a/courses/${courseData.id}/builder`)">
            <div class="flex items-center justify-center">
              <feather-icon icon="ZapIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4">Course<br>Builder</h2>
            </div>
          </vx-card>
          <vx-card class="bg-danger-gradient p-6 cursor-pointer" @click="refreshCourseCache(courseData.id)">
            <div class="flex items-center justify-center">
              <feather-icon icon="RefreshCwIcon" svgClasses="h-16 w-16"/>
            </div>
            <div class="flex items-center justify-center">
              <h2 class="text-center mt-4">Refresh<br>Cache</h2>
            </div>
          </vx-card>
        </div>
      </div>
      <!--- COURSE ACTION BAR END --->

      <!--- COURSE SHOW INFORMATION START--->
      <div class="vx-row mb-6 match-height">
        <div class="vx-col w-5/12">
          <vx-card class="w-full flex items-center">
            <h3 class="font-bold">Course Id</h3>
            <h3 class="font-light">{{ courseData.id }}</h3>
          </vx-card>
        </div>
        <div class="vx-col w-3/12">
          <vx-card class="w-full flex items-center">
            <h3 class="font-bold">Course Status</h3>
            <h3 class="capitalize font-light">{{ courseData.status }}</h3>
          </vx-card>
        </div>
        <div class="vx-col w-4/12 ">
          <vx-card class="p-1">
            <div class="grid grid-cols-2 gap-4 w-">
              <vs-button :to="{name: 'management-edit-course', params: { courseId: courseData.id }}"
                         class="w-full" icon="icon-edit" icon-pack="feather">Edit
              </vs-button>
              <vs-button color="danger" icon="icon-trash" icon-pack="feather" class="w-full"
                         @click="confirmDeleteCourse">Archive
              </vs-button>
            </div>
          </vx-card>
        </div>
      </div>

      <!--- POPUP ACTIONS START --->
      <vs-popup :active.sync="addStudentSearchActive" classContent="popup-example"
                title="Search for User by Name">

        <vs-table :data="addStudentSearchResults" :sst="true" max-items="5" search @search="addStudentUserSearch">
          <template slot="header">
            <h3>Users</h3>
          </template>

          <template slot="thead">
            <vs-th>Name</vs-th>
            <vs-th>Username</vs-th>
            <vs-th>Action</vs-th>
          </template>

          <template slot-scope="{data}">
            <vs-tr v-for="(attach) in data" :key="attach.id">

              <vs-td :data="`${attach.first_name} ${attach.last_name}`">
                {{ `${attach.first_name} ${attach.last_name}` }}
              </vs-td>

              <vs-td :data="attach.username">
                {{ attach.username }}
              </vs-td>

              <vs-td>
                <vs-button color="primary" size="small" type="border" @click="addStudentToCourseOptions(attach)">
                  Assign
                </vs-button>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vs-popup>
      <vs-popup :active.sync="addStudentAssignActive" :title="`Assign ${courseData.name} To User`">
        <div class="mb-4">
          <h6>Choose Subscription Expiry</h6>
          <datepicker :inline="true" placeholder="Select Date" v-model="addStudentAssignCourseCalendar"
                      class="datepicker-user-popup mt-4"></datepicker>
          <ul class="flex mt-5">
            <li>
              <vs-radio v-model="addStudentAssignCourseDuration" @input="calculateCalendarDate" vs-value="1"
                        class="mr-4">1 Month
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="addStudentAssignCourseDuration" @input="calculateCalendarDate" vs-value="3"
                        class="mr-4">3 Months
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="addStudentAssignCourseDuration" @input="calculateCalendarDate" vs-value="6"
                        class="mr-4">6 Months
              </vs-radio>
            </li>
            <li>
              <vs-radio v-model="addStudentAssignCourseDuration" @input="calculateCalendarDate" vs-value="12"
                        class="mr-4">1 Year
              </vs-radio>
            </li>
          </ul>
        </div>
        <p class="text-danger font-bold " v-if="addStudentAssignCourseCalendar === null">You Must Choose A
          Subscription
          Duration</p>

        <vs-button color="primary" type="filled"
                   @click="addStudentToCourseEnroll(addStudentAssignUser)"
                   v-if="addStudentAssignCourseCalendar">Assign Student
        </vs-button>

      </vs-popup>
      <!--- POPUP ACTIONS END --->

    </div>
  </div>
</template>

<script>
import Prism from 'vue-prism-component'
import vSelect from 'vue-select'
import Datepicker from 'vuejs-datepicker'

export default {
  data () {
    return {
      courseData: [],

      addStudentSearchActive: false,
      addStudentAssignActive: false,
      addStudentSearchResults: [],
      addStudentAssignUser: {},
      addStudentAssignCourseDuration: null,
      addStudentAssignCourseCalendar: null,

      courseDescriptionCounter: false,

      errorOccurred: false
    }
  },
  components: {
    Prism,
    vSelect,
    Datepicker
  },
  methods: {
    calculateCalendarDate () {
      const date = new Date()
      const months = parseInt(this.addStudentAssignCourseDuration)
      date.setMonth(date.getMonth() + months)
      this.addStudentAssignCourseCalendar = date
    },
    fetchCourseDetails (id) {

      this.$http.get(`courses/${id}`)
        .then(response => {

          if (response.status === 200) {
            this.courseData = response.data.data
          }

        })
        .catch(error => {
          this.errorOccurred = true
          this.error_msg = error
        })
    },
    confirmDeleteCourse () {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Archive',
        text: 'Please confirm you want to archive this course.',
        accept: this.deleteCourse,
        acceptText: 'Archive'
      })
    },
    deleteCourse () {
      this.$vs.loading()

      this.$http.delete(`courses/${this.$route.params.courseId}`)
        .then(response => {
          this.$vs.loading.close()

          if (response.status === 200) {
            this.$router.push('/a/courses')

            return this.$vs.notify({
              color: 'success',
              title: 'Course Archived',
              text: 'You have successfully archived this course',
              icon: 'feather',
              iconPack: 'icon-check-circle',
              position: 'top-right'
            })
          }
        })
        .catch(exception => {
          this.$vs.loading.close()

          let error = 'An unknown error occurred while deleting this course.'
          if (exception.response) {
            error = exception.response.data.error.description
          }

          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Delete Group',
            text: error,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    },
    addStudentUserSearch (term) {
      if (!term) return
      this.$http.get(`users?search=${term}&amount=5`)
        .then(response => {
          if (response.data) {
            this.addStudentSearchResults = response.data.data

          }
        })
    },
    addStudentToCourseOptions (id) {

      this.addStudentSearchActive = false
      this.addStudentAssignActive = true
      this.addStudentAssignUser = id
      this.addStudentAssignUser.profile_picture = this.addStudentAssignUser.profile_picture !== null ? this.addStudentAssignUser.profile_picture : 'default'

    },
    addStudentToCourseEnroll (user) {

      this.$vs.loading()

      if (!this.addStudentAssignCourseCalendar) {
        this.$vs.notify({
          color: 'danger',
          title: 'Failed to create Subscription',
          text: 'Please provide subscription duration.',
          icon: 'feather',
          iconPack: 'icon-x-circle',
          position: 'top-right'
        })
        return this.$vs.loading.close()
      }

      const pl = {
        user_id: user.id,
        course_id: this.courseData.id,
        status: 'active'
      }

      pl.ending_date = this.addStudentAssignCourseCalendar.getTime()

      this.$http.post('subscriptions/create', pl)
        .then(response => {
          this.$vs.loading.close()

          if (response.status === 200) {
            return this.$vs.notify({
              color: 'success',
              title: 'Subscription Created',
              text: 'You have successfully created a subscription',
              icon: 'feather',
              iconPack: 'icon-check-circle',
              position: 'top-right'
            })
          }

        }).catch(exception => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to create Subscription',
            text: exception.response.data.error.description,
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })

      this.addStudentAssignActive = false
      this.addStudentSearchActive = true

    },
    refreshCourseCache (id) {
      this.$vs.loading()

      this.$http.get(`courses/${id}/cache`)
        .then(response => {
          this.$vs.loading.close()

          if (response.status === 200) {
            return this.$vs.notify({
              color: 'success',
              title: 'Cache Cleared',
              text: 'The course cache has been cleared successfully',
              icon: 'feather',
              iconPack: 'icon-check-circle',
              position: 'top-right'
            })
          }

        })
        .catch(() => {
          this.$vs.loading.close()
          return this.$vs.notify({
            color: 'danger',
            title: 'Failed to Clear Cache',
            text: 'The course cache was not cleared successfully',
            icon: 'feather',
            iconPack: 'icon-x-circle',
            position: 'top-right'
          })
        })
    }
  },
  created () {
    this.fetchCourseDetails(this.$route.params.courseId)
  }
}

</script>

<style lang="scss">
#avatar-col {
  width: 10rem;
}

#page-user-view {

  table {
    td {
      vertical-align: top;
      min-width: 140px;
      padding-bottom: .8rem;
      word-break: break-all;
    }

    &:not(.permissions-table) {
      td {
        @media screen and (max-width: 370px) {
          display: block;
        }
      }
    }
  }

  @media (min-width: 1200px) and (max-width: 1700px) {
    .image-course {
      display: none;
    }
    .course-col {
      width: 0 !important;
      min-height: 2.05vh;
    }
    .welcome-col {
      width: 100% !important;
    }
  }
}

.datepicker-user-popup {
  .vdp-datepicker__calendar {
    width: 100%;
    padding: 5px 0 10px 0;
  }


  span.cell.day {
    border-radius: 5%;
    margin-bottom: 0.5%;
    margin-top: 0.5%;
  }

  span.cell.day.today {
    border-radius: 5%;
    color: #fff;
    background: rgba(var(--vs-primary), 0.1);

    &:hover {
      background: rgba(var(--vs-primary), 0.2);
    }
  }


}
</style>
